import { useCallback, useMemo, useRef, useState } from "react"
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  TextField,
  Typography,
} from "@mui/material"
import { OK, ERROR } from "helpers/data-statuses"
import { useLayouts, type Layout, type LayoutOwnerType } from "resources/layouts"
import { useCreateDashboard } from "../../hooks/use-create-dashboard"
import { useCanManageLayouts } from "../../hooks/use-can-manage-layouts"

export interface NewDashboardDialogProps {
  open: boolean
  onClose: () => void
}

// Tier display order + labels; "op" rows surface first as Templates.
const TIER_ORDER: { ownerType: LayoutOwnerType, label: string }[] = [
  { ownerType: "op", label: "Templates" },
  { ownerType: "user", label: "Your layouts" },
  { ownerType: "account", label: "Shared" },
]

interface LayoutGroup {
  ownerType: LayoutOwnerType
  label: string
  layouts: Layout[]
}

function groupByTier(layouts: Layout[]): LayoutGroup[] {
  return TIER_ORDER
    .map(({ ownerType, label }) => ({
      ownerType,
      label,
      layouts: layouts.filter((layout) => layout.ownerType === ownerType),
    }))
    .filter((group) => group.layouts.length > 0)
}

export default function NewDashboardDialog({ open, onClose }: NewDashboardDialogProps) {
  // Hold the fetch until the dialog is actually open — it mounts unconditionally alongside the
  // switcher, so an eager fetch here would fire for every multi-dashboard user's initial load. [APP-3309]
  const [layouts, status] = useLayouts(open)
  const { createDashboard, createDefaultDashboard, isContentSetsLoaded } = useCreateDashboard(open)
  // Picking a starting layout is superuser-only; regular users just name it (created from their own layout). [APP-3284]
  const canManageLayouts = useCanManageLayouts()

  const [name, setName] = useState("")
  const [selectedLayoutId, setSelectedLayoutId] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Re-entry latch: `isCreating` state flips next render, so two fast clicks could both pass a
  // state-only guard and create duplicates. The ref blocks the second in the same tick.
  const isCreatingRef = useRef(false)

  // The picker only renders for layout managers, so skip grouping/selection work for everyone else.
  const groups = useMemo(() => (canManageLayouts ? groupByTier(layouts ?? []) : []), [canManageLayouts, layouts])
  // Pre-select the first layout (derived, not stored, so it follows the loaded list). Then only a name
  // is required — Create isn't disabled while the user wonders why a layout pick was also needed.
  const firstLayoutId = groups[0]?.layouts[0]?.id ?? null
  const effectiveLayoutId = selectedLayoutId ?? firstLayoutId
  const selectedLayout = useMemo(
    () => (canManageLayouts ? (layouts ?? []).find((layout) => layout.id === effectiveLayoutId) ?? null : null),
    [canManageLayouts, layouts, effectiveLayoutId],
  )

  const reset = useCallback(() => {
    setName("")
    setSelectedLayoutId(null)
    setIsCreating(false)
    setError(null)
    isCreatingRef.current = false
  }, [])

  const handleClose = useCallback(() => {
    // While a create is in flight, ignore close so we never strand a half-built dashboard.
    if (isCreating) return
    reset()
    onClose()
  }, [isCreating, reset, onClose])

  const trimmedName = name.trim()
  // Name always required; a source layout only on the picker path — so cancelling early leaves no orphans.
  // The own-layout path (!canManageLayouts) can synthesize a default config from content-set
  // entitlements, which default false while loading — block Create until they're known, or an
  // entitled account can permanently persist a layout missing the thematic-betas panel. [APP-3309]
  const isCreateDisabled = trimmedName.length === 0 || isCreating
    || (canManageLayouts && selectedLayout === null)
    || (!canManageLayouts && !isContentSetsLoaded)

  const handleCreate = useCallback(async () => {
    if (trimmedName.length === 0 || isCreatingRef.current) return
    if (canManageLayouts && selectedLayout === null) return
    isCreatingRef.current = true
    setIsCreating(true)
    setError(null)
    try {
      // Picker path vs own-layout path. [APP-3284]
      if (canManageLayouts && selectedLayout) {
        await createDashboard(trimmedName, selectedLayout)
      } else {
        await createDefaultDashboard(trimmedName)
      }
      reset()
      onClose()
    } catch {
      // Keep the dialog open (with the chosen name/layout intact) so the user can retry. [APP-3309]
      setError("Could not create a new dashboard. Please try again.")
      setIsCreating(false)
      isCreatingRef.current = false
    }
  }, [trimmedName, canManageLayouts, selectedLayout, createDashboard, createDefaultDashboard, reset, onClose])

  // The layouts list degrades in-modal (spinner/inline error); neither blocks closing.
  const isListLoading = !(status === OK || status === ERROR)
  const isListError = status === ERROR

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>New dashboard</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <TextField
          autoFocus
          required
          fullWidth
          margin="dense"
          label="Dashboard name"
          helperText="What you'll call this dashboard."
          value={name}
          onChange={(event) => { setName(event.target.value) }}
          disabled={isCreating}
        />
        {canManageLayouts && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 1.5 }}>Starting layout</Typography>
            <Typography variant="caption" color="text.secondary">
              The panels and arrangement your new dashboard opens with.
            </Typography>
            {isListLoading && (
              <CircularProgress aria-label="Loading layouts" size={24} sx={{ display: "block", mx: "auto", my: 2 }} />
            )}
            {isListError && (
              <Alert severity="error" sx={{ my: 2 }}>Could not load starting points. Please try again.</Alert>
            )}
            {!isListLoading && !isListError && groups.length === 0 && (
              <Alert severity="info" sx={{ my: 2 }}>No starting points are available yet.</Alert>
            )}
            {!isListLoading && !isListError && groups.map((group) => (
              <List
                key={group.ownerType}
                dense
                subheader={<ListSubheader disableSticky>{group.label}</ListSubheader>}
              >
                {group.layouts.map((layout) => (
                  <ListItemButton
                    key={layout.id}
                    selected={layout.id === effectiveLayoutId}
                    disabled={isCreating}
                    onClick={() => { setSelectedLayoutId(layout.id) }}
                  >
                    <ListItemText primary={layout.name} />
                  </ListItemButton>
                ))}
              </List>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isCreating}>Cancel</Button>
        <Button onClick={() => { void handleCreate() }} disabled={isCreateDisabled} variant="contained">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  )
}
